import { fromMarkdown } from 'mdast-util-from-markdown';
import type { Definition, Link, Nodes, Root } from 'mdast';
import type { ConceptTerm } from '../../lookupApi';

/** Use the full Text view consistently for Chat's EUR-Lex HTML links.
 * Preserve the exact edition, language, anchor and any text highlight.
 * EUR-Lex can still miss a text-fragment jump on the first visit. */
export function eurLexTextDestination(href: string): string {
    try {
        const url = new URL(href);
        if (url.protocol === 'https:' && url.host === 'eur-lex.europa.eu'
            && !url.username && !url.password
            && /^\/legal-content\/[A-Z]{2}\/TXT\/HTML\/?$/.test(url.pathname)) {
            url.pathname = url.pathname.replace('/TXT/HTML', '/TXT');
            return url.href;
        }
    } catch { /* Non-URL links keep their existing behavior. */ }
    return href;
}

export function definitionTerm(href: string): string | null {
    try {
        const url = new URL(href);
        if (url.protocol !== 'https:' || url.host !== 'eur-lex.europa.eu' || url.username || url.password
            || !/^\/legal-content\/EN\/TXT(?:\/HTML)?\/?$/.test(url.pathname)
            || !/^CELEX:(?:0\d{4}[RL]\d{4,5}-\d{8}|3\d{4}[RL]\d{4,5})$/.test(url.searchParams.get('uri') || '')) return null;
        const fragment = /^#art_\d+[a-z]*:~:text=([^&]+)$/.exec(url.hash);
        const quoted = fragment && decodeURIComponent(fragment[1]);
        if (!quoted || quoted.length > 512) return null;
        const term = /^(?:‘([^’]+)’|'([^']+)')$/.exec(quoted);
        return term ? (term[1] || term[2]) : null;
    } catch { return null; }
}

export const nodeText = (node: Nodes): string => {
    if (node.type === 'text' || node.type === 'inlineCode') return node.value;
    return 'children' in node ? node.children.map(nodeText).join('') : '';
};

export function isArticleLabel(label: string): boolean {
    return /\b(?:art(?:icle)?s?\.?|section|paragraph|point)\s*\d|^\s*(?:https?:\/\/|\[?\d|§)/i.test(label);
}

/** Recover old chats from definition URLs too, even when the model put the
 * URL on an article number. Ambiguous names never choose an arbitrary act. */
export function collectConceptLinks(content: string, retrieved: ConceptTerm[] = [], scope = content): ConceptTerm[] {
    const links = new Map<string, Set<string>>();
    const add = (term: string, url: string) => {
        const key = term.trim().toLowerCase();
        if (key.length < 2 || key.length > 512 || isArticleLabel(key) || !definitionTerm(url)) return;
        if (!links.has(key)) links.set(key, new Set());
        links.get(key)!.add(url);
    };
    // Saved message metadata is optional, including in chats from older builds.
    if (Array.isArray(retrieved)) for (const row of retrieved) {
        if (row && typeof row.term === 'string' && typeof row.url === 'string') add(row.term, row.url);
    }
    const root = fromMarkdown(content);
    const definitions = new Map(root.children.filter((n): n is Definition => n.type === 'definition').map(n => [n.identifier, n.url]));
    const visit = (node: Nodes) => {
        const url = node.type === 'link' ? node.url : node.type === 'linkReference' ? definitions.get(node.identifier) : undefined;
        if (url) {
            const term = definitionTerm(url);
            if (term) {
                add(term, url);
                add(nodeText(node), url);
            }
        } else if ('children' in node) node.children.forEach(visit);
    };
    visit(root);
    // A section citing exactly one act can disambiguate names shared by
    // different acts. Never choose between editions or a mixed-act section.
    const act = (href: string): string | undefined => {
        try {
            const url = new URL(href);
            if (url.protocol !== 'https:' || url.host !== 'eur-lex.europa.eu') return undefined;
            const match = /^CELEX:[03](\d{4}[RL]\d{4,5})(?:-\d{8})?$/.exec(url.searchParams.get('uri') || '');
            return match?.[1];
        } catch { return undefined; }
    };
    const scopedRoot = fromMarkdown(scope);
    const scopedDefinitions = new Map(scopedRoot.children.filter((n): n is Definition => n.type === 'definition').map(n => [n.identifier, n.url]));
    const citedActs = new Set<string>();
    const cite = (node: Nodes) => {
        const url = node.type === 'link' ? node.url : node.type === 'linkReference' ? scopedDefinitions.get(node.identifier) : undefined;
        const id = url && act(url);
        if (id) citedActs.add(id);
        else if ('children' in node) node.children.forEach(cite);
    };
    cite(scopedRoot);
    // Keep an ambiguous phrase as an unlinked match so a shorter concept
    // inside it cannot accidentally select a different destination.
    return Array.from(links, ([term, urls]) => {
        const choices = Array.from(urls);
        const candidates = urls.size > 1 && citedActs.size === 1
            ? choices.filter(url => citedActs.has(act(url) || '')) : choices;
        return { term, url: candidates.length === 1 ? candidates[0] : '' };
    })
        .sort((a, b) => b.term.length - a.term.length);
}

const WORD = /[\p{L}\p{N}\p{Pd}_]/u;

export function conceptSegments(text: string, concepts: ConceptTerm[]): Array<{ text: string; url?: string }> {
    const parts: Array<{ text: string; url?: string }> = [];
    let plainStart = 0;
    for (let index = 0; index < text.length; index++) {
        const found = concepts.find(({ term }) => text.slice(index, index + term.length).toLowerCase() === term.toLowerCase()
            && !WORD.test(text[index - 1] || '') && !WORD.test(text[index + term.length] || ''));
        if (!found) continue;
        if (index > plainStart) parts.push({ text: text.slice(plainStart, index) });
        parts.push({ text: text.slice(index, index + found.term.length), url: found.url });
        index += found.term.length - 1;
        plainStart = index + 1;
    }
    if (plainStart < text.length) parts.push({ text: text.slice(plainStart) });
    return parts;
}

/** Transform prose text nodes only. Existing links, code, raw HTML and
 * references are preserved; the stored answer and its slice offsets stay intact. */
export function remarkConceptLinks(concepts: ConceptTerm[]) {
    return () => (root: Root) => {
        const definitions = new Map(root.children.filter((n): n is Definition => n.type === 'definition').map(n => [n.identifier, n.url]));
        const mark = (node: Nodes) => {
            const url = node.type === 'link' ? node.url : node.type === 'linkReference' ? definitions.get(node.identifier) : undefined;
            if (url && definitionTerm(url) && !isArticleLabel(nodeText(node))) {
                node.data = { ...node.data, hProperties: { ...node.data?.hProperties, 'data-concept': 'true' } };
            }
        };
        const walk = (node: Nodes) => {
            if (node.type === 'link' || node.type === 'linkReference') { mark(node); return; }
            if (!('children' in node)) return;
            node.children = (node.children as Nodes[]).flatMap<Nodes>(child => {
                if (child.type !== 'text') { walk(child); return [child]; }
                return conceptSegments(child.value, concepts).map(part => part.url ? {
                    type: 'link', url: part.url, children: [{ type: 'text', value: part.text }],
                    data: { hProperties: { 'data-concept': 'true' } },
                } as Link : { type: 'text', value: part.text } as const);
            }) as typeof node.children;
        };
        for (const node of root.children) {
            if ((node.type === 'heading' || node.type === 'paragraph') && /^(?:references|sources)(?:\s+(?:and|&)\s+gaps)?\s*:?$/i.test(nodeText(node).trim())) break;
            walk(node);
        }
    };
}
